import { useEffect, useState } from 'react';

import {
  getProjectWeeklyStatusPermissions,
  type ProjectWeeklyStatusPermissions,
} from '@/api/project-details/api';

export interface ProjectWeeklyStatusPermissionState {
  permissions: ProjectWeeklyStatusPermissions | null;
  loading: boolean;
  error: boolean;
}

const DENIED: ProjectWeeklyStatusPermissions = {
  can_create_weekly_status: false,
  can_update_weekly_status: false,
  can_view_weekly_status: false,
};

/** Fetch a fail-closed permission decision keyed by the active user token and project. */
export function useProjectWeeklyStatusPermissions(
  accessToken: string | null | undefined,
  clientId: string,
  projectId: string,
  enabled = true,
  userKey?: string | null,
): ProjectWeeklyStatusPermissionState {
  const [state, setState] = useState<ProjectWeeklyStatusPermissionState>({
    permissions: null,
    loading: enabled,
    error: false,
  });

  useEffect(() => {
    let active = true;
    if (!enabled || !accessToken || !clientId || !projectId) {
      setState({ permissions: DENIED, loading: false, error: false });
      return () => {
        active = false;
      };
    }

    setState({ permissions: null, loading: true, error: false });
    void getProjectWeeklyStatusPermissions(accessToken, clientId, projectId)
      .then((permissions) => {
        if (active) {
          setState({ permissions, loading: false, error: false });
        }
      })
      .catch(() => {
        if (active) {
          setState({ permissions: DENIED, loading: false, error: true });
        }
      });

    return () => {
      active = false;
    };
  }, [accessToken, clientId, projectId, enabled, userKey]);

  return state;
}
